import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { listMeasurements } from '../../services/measurements';

const PAGE_SIZE = 5;
const DEFAULT_TYPE = 'BLOOD_PRESSURE';

const alertReasonLabels = {
    STAGE_2_HYPERTENSION: 'Hipertensão estágio 2',
    SEVERE_HYPERTENSION: 'Hipertensão severa',
    HIGH_GLUCOSE: 'Glicose elevada',
    LOW_GLUCOSE: 'Glicose baixa',
    LOW_OXYGEN: 'Oxigénio no sangue baixo',
    HIGH_HR_AGE: 'Frequência cardíaca elevada'
};

const MEASUREMENT_TYPE_OPTIONS = [
    { key: 'BLOOD_PRESSURE', label: 'Pressão Arterial' },
    { key: 'BLOOD_SUGAR', label: 'Glicemia' },
    { key: 'BODY_TEMPERATURE', label: 'Temperatura' },
    { key: 'HEARTRATE', label: 'Frequência Cardíaca' },
    { key: 'BLOOD_OXYGEN', label: 'Oxigénio no Sangue' },
    { key: 'WEIGHT', label: 'Peso' },
    { key: 'EXERCISE', label: 'Exercício' }
];

const CHIP_BASE_SX = {
    minHeight: 38,
    borderRadius: '999px',
    fontWeight: 700,
    borderWidth: '2px'
};

const tableCardSx = {
    p: { xs: 1.8, md: 2.2 },
    borderRadius: '20px',
    bgcolor: '#FFFFFF',
    boxShadow: 'none'
};

const headerSx = (gridTemplateColumns) => ({
    px: 2.2,
    py: 1.1,
    display: 'grid',
    gridTemplateColumns,
    gap: 1.2,
    bgcolor: '#3D3D3D',
    color: '#fff',
    borderRadius: '10px',
    boxShadow: 'none'
});

const rowSx = (gridTemplateColumns) => ({
    px: 2.2,
    py: 1.35,
    display: 'grid',
    gridTemplateColumns,
    gap: 1.2,
    alignItems: 'center',
    bgcolor: '#ECECEC',
    borderRadius: '12px',
    boxShadow: 'none'
});

const getValue = (data, keys, fallback = '--') => {
    for (const key of keys) {
        if (data?.[key] != null && data?.[key] !== '') return data[key];
    }
    return fallback;
};

const withUnit = (value, unit) => {
    if (value == null || value === '' || value === '--') return '--';
    return `${value} ${unit}`;
};

const toPtDateTime = (measurement) => {
    const raw = measurement?.ts || measurement?.created || measurement?.data?.ts || null;
    if (!raw) return '--';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toLocaleString('pt-PT');
};

const inferOrigin = (measurement) => {
    const source = String(
        measurement?.source ||
            measurement?.origin ||
            measurement?.mode ||
            measurement?.data?.source ||
            ''
    ).toUpperCase();

    if (!source) return '--';
    if (source.includes('AUTO')) return 'Auto';
    if (source.includes('MANUAL')) return 'Manual';
    return source;
};

const normalizeAlertReason = (value) => {
    const key = String(value || '').trim().toUpperCase();
    if (!key) {
        return '';
    }

    return alertReasonLabels[key] || key;
};

const measurementConfigMap = {
    BLOOD_PRESSURE: {
        gridTemplateColumns: '1.8fr 1fr 1fr 0.9fr',
        columns: [
            { key: 'date', label: 'Data' },
            { key: 'systolic', label: 'Sistólica' },
            { key: 'diastolic', label: 'Diastólica' },
            { key: 'hr', label: 'FC' }
        ],
        mapRow: (measurement) => {
            const data = measurement?.data || {};
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: toPtDateTime(measurement),
                systolic: withUnit(getValue(data, ['systolic']), 'mmHg'),
                diastolic: withUnit(getValue(data, ['diastolic']), 'mmHg'),
                hr: withUnit(getValue(data, ['hr']), 'bpm'),
                origin: inferOrigin(measurement),
                inAlert: Boolean(measurement?.in_alert),
                alertReason: normalizeAlertReason(measurement?.alert_reason),
                alertTargetKey: 'systolic'
            };
        }
    },
    BLOOD_SUGAR: {
        gridTemplateColumns: '1.8fr 1fr',
        columns: [
            { key: 'date', label: 'Data' },
            { key: 'glucose', label: 'Glicose' }
        ],
        mapRow: (measurement) => {
            const data = measurement?.data || {};
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: toPtDateTime(measurement),
                glucose: withUnit(getValue(data, ['glucose']), 'mg/dL'),
                origin: inferOrigin(measurement),
                inAlert: Boolean(measurement?.in_alert),
                alertReason: normalizeAlertReason(measurement?.alert_reason),
                alertTargetKey: 'glucose'
            };
        }
    },
    BODY_TEMPERATURE: {
        gridTemplateColumns: '1.8fr 1fr',
        columns: [
            { key: 'date', label: 'Data' },
            { key: 'temperature', label: 'Temperatura' }
        ],
        mapRow: (measurement) => {
            const data = measurement?.data || {};
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: toPtDateTime(measurement),
                temperature: withUnit(getValue(data, ['temperature']), 'ºC'),
                origin: inferOrigin(measurement),
                inAlert: Boolean(measurement?.in_alert),
                alertReason: normalizeAlertReason(measurement?.alert_reason),
                alertTargetKey: 'temperature'
            };
        }
    },
    HEARTRATE: {
        gridTemplateColumns: '1.8fr 1fr',
        columns: [
            { key: 'date', label: 'Data' },
            { key: 'hr', label: 'FC' }
        ],
        mapRow: (measurement) => {
            const data = measurement?.data || {};
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: toPtDateTime(measurement),
                hr: withUnit(getValue(data, ['hr']), 'bpm'),
                origin: inferOrigin(measurement),
                inAlert: Boolean(measurement?.in_alert),
                alertReason: normalizeAlertReason(measurement?.alert_reason),
                alertTargetKey: 'hr'
            };
        }
    },
    BLOOD_OXYGEN: {
        gridTemplateColumns: '1.8fr 1fr 1fr',
        columns: [
            { key: 'date', label: 'Data' },
            { key: 'saturation', label: 'Saturação' },
            { key: 'hr', label: 'FC' }
        ],
        mapRow: (measurement) => {
            const data = measurement?.data || {};
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: toPtDateTime(measurement),
                saturation: withUnit(getValue(data, ['saturation']), '%'),
                hr: withUnit(getValue(data, ['hr']), 'bpm'),
                origin: inferOrigin(measurement),
                inAlert: Boolean(measurement?.in_alert),
                alertReason: normalizeAlertReason(measurement?.alert_reason),
                alertTargetKey: 'saturation'
            };
        }
    },
    WEIGHT: {
        gridTemplateColumns: '1.8fr 1fr',
        columns: [
            { key: 'date', label: 'Data' },
            { key: 'weight', label: 'Peso' }
        ],
        mapRow: (measurement) => {
            const data = measurement?.data || {};
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: toPtDateTime(measurement),
                weight: withUnit(getValue(data, ['weight']), 'kg'),
                origin: inferOrigin(measurement),
                inAlert: Boolean(measurement?.in_alert),
                alertReason: normalizeAlertReason(measurement?.alert_reason),
                alertTargetKey: 'weight'
            };
        }
    },
    EXERCISE: {
        gridTemplateColumns: '1.8fr 1.4fr 1fr',
        columns: [
            { key: 'date', label: 'Data' },
            { key: 'activity', label: 'Atividade' },
            { key: 'duration', label: 'Duração (min)' }
        ],
        mapRow: (measurement) => {
            const data = measurement?.data || {};
            return {
                id: measurement?.uuid || Math.random().toString(36),
                date: toPtDateTime(measurement),
                activity: getValue(data, ['activity']),
                duration: withUnit(getValue(data, ['durationMin']), 'min'),
                origin: inferOrigin(measurement),
                inAlert: Boolean(measurement?.in_alert),
                alertReason: normalizeAlertReason(measurement?.alert_reason),
                alertTargetKey: 'activity'
            };
        }
    }
};

const renderValueWithAlert = (row, columnKey, value) => {
    const shouldShowAlert = row?.inAlert && row?.alertTargetKey === columnKey;

    if (!shouldShowAlert) {
        return value ?? '--';
    }

    return (
        <Stack direction="row" spacing={0.75} alignItems="center">
            <Tooltip title={row.alertReason || 'Medição com alerta'}>
                <Box
                    component="img"
                    src="/assets/logos-svg/alertAmarelo.svg"
                    alt="Alerta"
                    sx={{ width: 16, height: 16, display: 'block', flexShrink: 0 }}
                />
            </Tooltip>
            <span>{value ?? '--'}</span>
        </Stack>
    );
};

const CaregiverMonitoringSection = ({
    caregiverName,
    patientName,
    userRoleUuid,
    subjectRoleUuid,
    initialMeasurementType
}) => {
    const [selectedType, setSelectedType] = useState(DEFAULT_TYPE);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageTokens, setPageTokens] = useState({ 0: null });
    const [hasNextPage, setHasNextPage] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        setPageIndex(0);
        setPageTokens({ 0: null });
        setHasNextPage(false);
    }, [selectedType, subjectRoleUuid, userRoleUuid]);

    useEffect(() => {
        if (
            initialMeasurementType &&
            MEASUREMENT_TYPE_OPTIONS.some((option) => option.key === initialMeasurementType)
        ) {
            setSelectedType(initialMeasurementType);
        }
    }, [initialMeasurementType]);

    const currentToken = pageTokens[pageIndex] || null;

    useEffect(() => {
        const loadMeasurements = async () => {
            if (!userRoleUuid || !subjectRoleUuid) {
                setRows([]);
                return;
            }

            setLoading(true);
            try {
                const response = await listMeasurements({
                    userRoleUuid,
                    subjectRoleUuid,
                    measurementTypes: [selectedType],
                    order: 'DESC',
                    paginationLimit: PAGE_SIZE,
                    paginationToken: currentToken
                });

                const sourceRows = Array.isArray(response?.data) ? response.data : [];
                const config = measurementConfigMap[selectedType] || measurementConfigMap[DEFAULT_TYPE];
                const responseTotalCount = Number(response?.headers?.['pagination-count'] || 0);
                setTotalCount(responseTotalCount);
                setRows(sourceRows.map((item) => config.mapRow(item)));

                const nextToken = response?.headers?.['pagination-token'] || null;
                const nextPageStart = (pageIndex + 1) * PAGE_SIZE;
                const canAdvance = Boolean(nextToken) && nextPageStart < responseTotalCount;
                setHasNextPage(canAdvance);

                setPageTokens((prev) => {
                    const next = { 0: null };

                    for (let index = 1; index <= pageIndex; index += 1) {
                        if (prev[index] !== undefined) {
                            next[index] = prev[index];
                        }
                    }

                    if (canAdvance) {
                        next[pageIndex + 1] = nextToken;
                    }

                    const prevKeys = Object.keys(prev);
                    const nextKeys = Object.keys(next);
                    if (
                        prevKeys.length === nextKeys.length &&
                        prevKeys.every((key) => prev[key] === next[key])
                    ) {
                        return prev;
                    }

                    return next;
                });
            } catch {
                setRows([]);
                setHasNextPage(false);
                setTotalCount(0);
            } finally {
                setLoading(false);
            }
        };

        void loadMeasurements();
    }, [currentToken, pageIndex, selectedType, subjectRoleUuid, userRoleUuid]);

    const selectedConfig =
        measurementConfigMap[selectedType] || measurementConfigMap[DEFAULT_TYPE];
    const totalPages = useMemo(() => {
        if (totalCount <= 0) {
            return 1;
        }

        return Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    }, [totalCount]);

    return (
        <Stack spacing={2.5}>
            <Paper variant="elevation=0" sx={tableCardSx}>
                <Typography variant="h5" fontWeight={700} color="#3D3D3D">
                    Monitorização: Últimos Registos do Paciente
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" mt={2.2} mb={2}>
                    {MEASUREMENT_TYPE_OPTIONS.map((option) => {
                        const isSelected = selectedType === option.key;

                        return (
                            <Chip
                                key={option.key}
                                label={option.label}
                                clickable
                                onClick={() => setSelectedType(option.key)}
                                variant="outlined"
                                sx={{
                                    ...CHIP_BASE_SX,
                                    color: isSelected ? '#D63731' : '#3D3D3D',
                                    borderColor: isSelected ? '#D63731' : '#D9D9D9',
                                    bgcolor: isSelected ? 'rgba(214, 55, 49, 0.05)' : '#FFFFFF',
                                    '&:hover': {
                                        bgcolor: isSelected
                                            ? 'rgba(214, 55, 49, 0.05)'
                                            : '#F7F7F7'
                                    }
                                }}
                            />
                        );
                    })}
                </Stack>

                <Stack spacing={1}>
                    <Paper sx={headerSx(selectedConfig.gridTemplateColumns)}>
                        {selectedConfig.columns.map((column) => (
                            <Typography key={column.key} variant="caption" fontWeight={600}>
                                {column.label}
                            </Typography>
                        ))}
                    </Paper>

                    {loading ? (
                        <Paper
                            sx={{
                                ...rowSx(selectedConfig.gridTemplateColumns),
                                minHeight: 56
                            }}
                        >
                            <Typography
                                sx={{ gridColumn: '1 / -1' }}
                                variant="body2"
                                color="text.secondary"
                                fontWeight={600}
                                textAlign="center"
                            >
                                A carregar medições...
                            </Typography>
                        </Paper>
                    ) : null}

                    {!loading && rows.length === 0 ? (
                        <Paper
                            sx={{
                                ...rowSx(selectedConfig.gridTemplateColumns),
                                minHeight: 56
                            }}
                        >
                            <Typography
                                sx={{ gridColumn: '1 / -1' }}
                                variant="body2"
                                color="text.secondary"
                                fontWeight={600}
                                textAlign="center"
                            >
                                Sem registos para esta métrica.
                            </Typography>
                        </Paper>
                    ) : null}

                    {!loading
                        ? rows.map((row) => (
                              <Paper key={row.id} sx={rowSx(selectedConfig.gridTemplateColumns)}>
                                  {selectedConfig.columns.map((column, index) => (
                                      <Typography
                                          key={`${row.id}-${column.key}`}
                                          variant="body2"
                                          fontWeight={index === 0 ? 700 : 600}
                                          color={index === 0 ? '#3D3D3D' : 'text.secondary'}
                                      >
                                          {renderValueWithAlert(row, column.key, row[column.key])}
                                      </Typography>
                                  ))}
                              </Paper>
                          ))
                        : null}
                </Stack>

                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={0.5}
                    mt={1.2}
                >
                    <Tooltip title="Página anterior">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                                disabled={pageIndex <= 0}
                            >
                                <KeyboardArrowLeftIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ minWidth: 22, textAlign: 'center' }}
                    >
                        {`${pageIndex + 1} de ${totalPages}`}
                    </Typography>
                    <Tooltip title="Página seguinte">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    if (hasNextPage) {
                                        setPageIndex((prev) => prev + 1);
                                    }
                                }}
                                disabled={!hasNextPage}
                            >
                                <KeyboardArrowRightIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>
            </Paper>
        </Stack>
    );
};

export default CaregiverMonitoringSection;
